import type { LicenceRestriction, LicenceRuntime } from './LicenceRuntime';
import { resolveStatusEndpoints } from './activationConstants';
import { SecureLicenceStore } from './SecureLicenceStore';
import { verifyServerToken } from './ServerTokenVerifier';
import { NetworkStatusService } from '@infra/network/NetworkStatusService';
import { logger } from '@infra/telemetry/Logger';

/** Contrôle de statut serveur : 1× / semaine (ADR-003). */
export const STATUS_CHECK_INTERVAL_MS = 7 * 24 * 3600 * 1000;
/** Une licence suspendue est revérifiée chaque jour pour lever vite la suspension. */
export const STATUS_RECHECK_WHEN_RESTRICTED_MS = 24 * 3600 * 1000;
/** Sans contrôle réussi au-delà de 30 jours : lecture seule (ADR-003). */
export const OFFLINE_GRACE_MS = 30 * 24 * 3600 * 1000;
/** Tolérance d'horloge entre le poste et le serveur pour comparer `iat` à la détection. */
const STATUS_IAT_TOLERANCE_MS = 120_000;

export const RESTRICTION_MESSAGES: Record<LicenceRestriction['kind'], string> = {
  revoked:
    'Cette licence a été révoquée. Vos données restent consultables et exportables, mais la saisie est bloquée. Contactez Nexxus.',
  suspended:
    'Cette licence a été suspendue. Vos données restent consultables et exportables, mais la saisie est bloquée. Contactez Nexxus.',
  offline_too_long:
    'Aucun contrôle de licence n’a pu être effectué depuis plus de 30 jours. L’application est en lecture seule : connectez ce poste à Internet pour reprendre la saisie.',
};

/** Licence déclarée expirée par le serveur : même effet qu'une suspension (lecture seule), message dédié. */
export const EXPIRED_MESSAGE =
  'Cette licence a expiré. Vos données restent consultables et exportables, mais la saisie est bloquée. Contactez Nexxus pour la renouveler.';

const REFUSAL_CODES: Record<string, 'revoked' | 'suspended'> = {
  license_revoked: 'revoked',
  activation_revoked: 'revoked',
  license_suspended: 'suspended',
  license_expired: 'suspended',
};

type Notifier = (restriction: LicenceRestriction) => void;

/**
 * Révocation / suspension / hors ligne prolongé (ADR-003).
 * Ne supprime jamais de données : seule l'écriture est bloquée via le mode lecture seule du runtime.
 */
export class LicenceStatusService {
  private static runtime: LicenceRuntime | null = null;
  private static notifier: Notifier | null = null;
  private static watching = false;
  private static timer: ReturnType<typeof setInterval> | null = null;
  private static inFlight: Promise<StatusCheckResult> | null = null;

  static setNotifier(fn: Notifier | null): void {
    LicenceStatusService.notifier = fn;
  }

  /**
   * Code d'erreur stable d'un corps de réponse 403. Le serveur répond `{ error: <message FR>, code }` :
   * `code` (puis `error_code`) est prioritaire ; `error` n'est lu qu'en dernier (ancien format où
   * il portait le code), jamais en premier, car c'est un libellé lisible, pas un code.
   */
  static extractErrorCode(body: unknown): string | undefined {
    if (typeof body !== 'object' || body === null) return undefined;
    const b = body as Record<string, unknown>;
    for (const key of ['code', 'error_code', 'error']) {
      if (typeof b[key] === 'string') return b[key] as string;
    }
    return undefined;
  }

  /**
   * Réaction à un 403 de renew / entitlements / status POUR UNE ENTREPRISE (P0-3).
   * Sans `tenantId` : entreprise active du runtime. Révoquer A ne touche jamais B.
   */
  static async handleServerRefusal(code: string | undefined, tenantId?: string): Promise<void> {
    const kind = code ? REFUSAL_CODES[code] : undefined;
    if (!kind) {
      logger.warn({ event: 'licence_refusal_ignored', code: code ?? 'inconnu' });
      return;
    }
    const target = tenantId ?? LicenceStatusService.activeTenantId();
    if (!target) return;
    await LicenceStatusService.persistAndApply(
      kind,
      target,
      code === 'license_expired' ? EXPIRED_MESSAGE : undefined,
    );
  }

  /** Entreprise active du runtime (celle dont le certificat est chargé). */
  private static activeTenantId(runtime = LicenceStatusService.runtime): string | null {
    return runtime?.getState().claims?.tenant_id ?? null;
  }

  /**
   * Certificats à contrôler : tout le portefeuille du poste (une entrée par entreprise).
   * Repli : le certificat actif seul quand le portefeuille n'est pas encore constitué.
   */
  static async listCertificates(runtime: LicenceRuntime): Promise<Array<[string, string]>> {
    const portfolio = await SecureLicenceStore.getActivationPortfolio();
    const entries = Object.entries(portfolio);
    if (entries.length > 0) return entries;
    const active = LicenceStatusService.activeTenantId(runtime);
    const cert = await SecureLicenceStore.getActivationCertificate();
    return active && cert ? [[active, cert]] : [];
  }

  /**
   * Après une bascule d'entreprise : applique au runtime la restriction PROPRE à la nouvelle
   * entreprise active (levée si elle n'en a pas), puis réévalue l'absence prolongée de contrôle.
   */
  static async refreshForActiveTenant(runtime: LicenceRuntime): Promise<void> {
    LicenceStatusService.runtime = runtime;
    const tenantId = LicenceStatusService.activeTenantId(runtime);
    if (!tenantId) return;
    const stored = parseStored(await SecureLicenceStore.getLicenceStatus(tenantId));
    if (stored) {
      runtime.setRestriction(stored);
      return;
    }
    runtime.setRestriction(null);
    await LicenceStatusService.evaluateOffline(runtime, false);
  }

  /** Recharge l'état persisté (démarrage) puis évalue la règle des 30 jours. */
  static async restore(runtime: LicenceRuntime): Promise<void> {
    LicenceStatusService.runtime = runtime;
    const tenantId = LicenceStatusService.activeTenantId(runtime);
    const raw = tenantId ? await SecureLicenceStore.getLicenceStatus(tenantId) : null;
    const stored = parseStored(raw);
    if (stored) {
      LicenceStatusService.apply(stored, false);
      return;
    }
    await LicenceStatusService.evaluateOffline(runtime, false);
  }

  /** Lecture seule « hors ligne prolongé » si aucun contrôle réussi depuis 30 jours. */
  static async evaluateOffline(runtime: LicenceRuntime, notify = true): Promise<void> {
    const current = runtime.getRestriction();
    if (current && current.kind !== 'offline_too_long') return;

    const tenantId = LicenceStatusService.activeTenantId(runtime);
    if (!tenantId) return;
    const baseline = await latestSuccessfulContact(tenantId);
    if (baseline === null) {
      // Première exécution : le compteur des 30 jours démarre maintenant.
      await SecureLicenceStore.setLastStatusCheckAt(tenantId, new Date().toISOString());
      return;
    }
    const expired = Date.now() - baseline > OFFLINE_GRACE_MS;
    if (expired && !current) {
      LicenceStatusService.apply(
        {
          kind: 'offline_too_long',
          message: RESTRICTION_MESSAGES.offline_too_long,
          since: new Date().toISOString(),
        },
        notify,
      );
      logger.warn({ event: 'licence_offline_too_long' });
    } else if (!expired && current) {
      runtime.setRestriction(null);
    }
  }

  /**
   * Contrôle de statut (/v1/activations/status). Hebdomadaire sauf `force`.
   * Un `active` signé lève la suspension ; un échec réseau ne change rien.
   */
  static checkStatus(
    runtime: LicenceRuntime,
    options: { force?: boolean } = {},
  ): Promise<StatusCheckResult> {
    if (LicenceStatusService.inFlight) return LicenceStatusService.inFlight;
    LicenceStatusService.inFlight = LicenceStatusService.runCheck(runtime, options).finally(() => {
      LicenceStatusService.inFlight = null;
    });
    return LicenceStatusService.inFlight;
  }

  /** Démarrage + retour réseau + minuterie : vérifie quand le délai est écoulé. */
  static watch(runtime: LicenceRuntime): void {
    LicenceStatusService.runtime = runtime;
    void LicenceStatusService.tick(runtime);
    if (LicenceStatusService.watching) return;
    LicenceStatusService.watching = true;
    NetworkStatusService.subscribe((online) => {
      if (online && LicenceStatusService.runtime) {
        void LicenceStatusService.tick(LicenceStatusService.runtime);
      }
    });
    LicenceStatusService.timer = setInterval(
      () => {
        if (LicenceStatusService.runtime)
          void LicenceStatusService.tick(LicenceStatusService.runtime);
      },
      6 * 3600 * 1000,
    );
  }

  private static async tick(runtime: LicenceRuntime): Promise<void> {
    await LicenceStatusService.checkStatus(runtime);
    await LicenceStatusService.evaluateOffline(runtime);
  }

  private static async runCheck(
    runtime: LicenceRuntime,
    options: { force?: boolean },
  ): Promise<StatusCheckResult> {
    LicenceStatusService.runtime = runtime;
    const activeId = LicenceStatusService.activeTenantId(runtime);
    if (!runtime.getState().claims || !activeId) return 'skipped';
    if (!NetworkStatusService.isBrowserOnline()) return 'offline';

    const fingerprint = await SecureLicenceStore.getDeviceFingerprint();
    if (!fingerprint) return 'skipped';

    // Chaque entreprise du portefeuille est contrôlée avec SON certificat (P0-3).
    let activeResult: StatusCheckResult = 'skipped';
    for (const [tenantId, certificate] of await LicenceStatusService.listCertificates(runtime)) {
      const result = await LicenceStatusService.checkTenant(
        runtime,
        tenantId,
        certificate,
        fingerprint,
        options,
      );
      if (tenantId === activeId) activeResult = result;
    }
    return activeResult;
  }

  private static async checkTenant(
    runtime: LicenceRuntime,
    tenantId: string,
    certificate: string,
    fingerprint: string,
    options: { force?: boolean },
  ): Promise<StatusCheckResult> {
    const restricted = parseStored(await SecureLicenceStore.getLicenceStatus(tenantId));
    if (!options.force) {
      const last = await SecureLicenceStore.getLastStatusCheckAt(tenantId);
      const effective = restricted ? STATUS_RECHECK_WHEN_RESTRICTED_MS : STATUS_CHECK_INTERVAL_MS;
      if (last && Date.now() - Date.parse(last) < effective) return 'skipped';
    }

    for (const endpoint of resolveStatusEndpoints()) {
      const posted = await postJson(endpoint, { certificate, device_fingerprint: fingerprint });
      if (!posted) continue;
      if (posted.status === 403) {
        await LicenceStatusService.handleServerRefusal(
          LicenceStatusService.extractErrorCode(posted.data),
          tenantId,
        );
        return 'refused';
      }
      if (!posted.ok) continue;

      const verified = await verifyServerToken(posted.data.status_jwt, 'status', {
        deviceFingerprint: fingerprint,
      });
      if (!verified.ok) {
        logger.warn({ event: 'licence_status_rejected', reason: verified.reason });
        continue;
      }
      // Le statut signé doit viser CETTE entreprise (jamais celui d'une autre).
      const claimTenant = verified.payload.tenant_id;
      if (typeof claimTenant === 'string' && claimTenant !== tenantId) {
        logger.warn({ event: 'licence_status_wrong_tenant' });
        continue;
      }
      return LicenceStatusService.applyVerifiedStatus(runtime, tenantId, verified.payload);
    }
    return 'unreachable';
  }

  private static async applyVerifiedStatus(
    runtime: LicenceRuntime,
    tenantId: string,
    payload: { status?: unknown; iat?: number },
  ): Promise<StatusCheckResult> {
    const isActive = LicenceStatusService.activeTenantId(runtime) === tenantId;
    const status = payload.status;
    if (status === 'revoked' || status === 'suspended') {
      await SecureLicenceStore.setLastStatusCheckAt(tenantId, new Date().toISOString());
      await LicenceStatusService.persistAndApply(status, tenantId);
      return status;
    }
    if (status === 'expired') {
      await SecureLicenceStore.setLastStatusCheckAt(tenantId, new Date().toISOString());
      await LicenceStatusService.persistAndApply('suspended', tenantId, EXPIRED_MESSAGE);
      return 'suspended';
    }
    if (status !== 'active') {
      logger.warn({ event: 'licence_status_unknown', status: String(status) });
      return 'unreachable';
    }

    const liveRestriction = isActive ? runtime.getRestriction() : null;
    const current =
      (liveRestriction && liveRestriction.kind !== 'offline_too_long' ? liveRestriction : null) ??
      parseStored(await SecureLicenceStore.getLicenceStatus(tenantId));
    if (current) {
      // Anti-rejeu : un « active » émis avant la détection ne lève pas la restriction.
      const issuedMs = (payload.iat ?? 0) * 1000;
      if (issuedMs < Date.parse(current.since) - STATUS_IAT_TOLERANCE_MS) {
        logger.warn({ event: 'licence_status_replay_refused' });
        return 'refused';
      }
    }
    await SecureLicenceStore.setLastStatusCheckAt(tenantId, new Date().toISOString());
    await SecureLicenceStore.clearLicenceStatus(tenantId);
    if (isActive) {
      const live = runtime.getRestriction();
      runtime.setRestriction(null);
      if (live) logger.info({ event: 'licence_restriction_lifted', kind: live.kind });
    }
    return 'active';
  }

  private static async persistAndApply(
    kind: 'revoked' | 'suspended',
    tenantId: string,
    message?: string,
  ): Promise<void> {
    const existing = parseStored(await SecureLicenceStore.getLicenceStatus(tenantId));
    const restriction: LicenceRestriction =
      existing?.kind === kind
        ? existing
        : {
            kind,
            message: message ?? RESTRICTION_MESSAGES[kind],
            since: new Date().toISOString(),
          };
    await SecureLicenceStore.setLicenceStatus(tenantId, JSON.stringify(restriction));
    logger.warn({ event: 'licence_restricted', kind, tenantId });
    // Seule l'entreprise ACTIVE passe en lecture seule ; les autres le seront à l'ouverture.
    if (LicenceStatusService.activeTenantId() !== tenantId) return;
    const alreadyShown = LicenceStatusService.runtime?.getRestriction()?.kind === kind;
    LicenceStatusService.apply(restriction, existing?.kind !== kind && !alreadyShown);
  }

  private static apply(restriction: LicenceRestriction, notify = true): void {
    const runtime = LicenceStatusService.runtime;
    if (!runtime) return;
    runtime.setRestriction(restriction);
    if (notify) LicenceStatusService.notifier?.(restriction);
  }

  static __resetForTests(): void {
    if (LicenceStatusService.timer) clearInterval(LicenceStatusService.timer);
    LicenceStatusService.timer = null;
    LicenceStatusService.runtime = null;
    LicenceStatusService.notifier = null;
    LicenceStatusService.watching = false;
    LicenceStatusService.inFlight = null;
  }
}

export type StatusCheckResult =
  'active' | 'revoked' | 'suspended' | 'refused' | 'offline' | 'unreachable' | 'skipped';

function parseStored(raw: string | null): LicenceRestriction | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as Partial<LicenceRestriction>;
    if ((v.kind === 'revoked' || v.kind === 'suspended') && typeof v.since === 'string') {
      return { kind: v.kind, since: v.since, message: RESTRICTION_MESSAGES[v.kind] };
    }
  } catch {
    /* état illisible : ignoré */
  }
  return null;
}

async function latestSuccessfulContact(tenantId: string): Promise<number | null> {
  const values = await Promise.all([
    SecureLicenceStore.getLastStatusCheckAt(tenantId),
    SecureLicenceStore.getLastVerifiedAt(tenantId),
    SecureLicenceStore.getLastRenewalAt(tenantId),
  ]);
  const times = values.map((v) => (v ? Date.parse(v) : Number.NaN)).filter(Number.isFinite);
  return times.length ? Math.max(...times) : null;
}

async function postJson(
  endpoint: string,
  body: Record<string, unknown>,
): Promise<{ ok: boolean; status: number; data: Record<string, unknown> } | null> {
  if (window.electron?.activateRemote) {
    try {
      return await window.electron.activateRemote(endpoint, body);
    } catch {
      return null;
    }
  }
  try {
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(15_000),
    });
    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    return { ok: res.ok, status: res.status, data };
  } catch {
    return null;
  }
}
